import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'
import { supabase } from './auth/supabase.ts'
import { initSentry, setUser } from './integrations/sentry'
import { identifyUser } from './integrations/klaviyo'

// Import the generated route tree
import { routeTree } from './routeTree.gen.ts'

// Initialize Sentry (production only)
initSentry()

const TanStackQueryProviderContext = TanStackQueryProvider.getContext()

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  Wrap: (props: { children: React.ReactNode }) => {
    return (
      <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
        {props.children}
      </TanStackQueryProvider.Provider>
    )
  },
})

// Listen for external auth state changes (token refresh, session expiry, other tabs)
// and invalidate the router so beforeLoad re-fetches the session
supabase.auth.onAuthStateChange((_, session) => {
  router.invalidate()

  // Set Sentry user context
  if (session?.user?.email) {
    setUser({ email: session.user.email, id: session.user.id })
  } else {
    setUser(null)
  }

  // Identify user in Klaviyo
  if (session?.user?.email) {
    identifyUser(session.user.email)
  }
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  )
}

reportWebVitals()
